import * as React from 'react';
import type { Key } from 'react-aria-components';
import {
  Alert,
  Button,
  ComboBox,
  Description,
  EmptyState,
  Input,
  ListBox,
  Label,
  Modal,
  Spinner,
} from '@heroui/react';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

type AMapNamespace = any;

declare global {
  interface Window {
    AMap?: AMapNamespace;
    __APP_ENV__?: {
      VITE_AMAP_KEY?: string;
      VITE_AMAP_SECURITY_JSCODE?: string;
      VITE_AMAP_SECURITY_JS_CODE?: string;
    };
    _AMapSecurityConfig?: {
      securityJsCode?: string;
      serviceHost?: string;
    };
  }
}

let amapScriptPromise: Promise<AMapNamespace> | null = null;

function sleep(ms: number) {
  return new Promise<void>((r) => setTimeout(r, ms));
}

function loadAmapJs(amapKey: string, securityJsCode?: string): Promise<AMapNamespace> {
  if (typeof window === 'undefined') return Promise.reject(new Error('window is undefined'));

  // 如果已可用，直接复用
  if (window.AMap && typeof window.AMap.AutoComplete === 'function') return Promise.resolve(window.AMap);

  if (amapScriptPromise) return amapScriptPromise;

  if (securityJsCode) {
    window._AMapSecurityConfig = {
      ...(window._AMapSecurityConfig || {}),
      securityJsCode,
    };
  }

  amapScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-his-amap-loader="1"]');
    if (existing) {
      // 如果脚本早已加载，直接复用全局 AMap，避免挂载 load 事件导致 promise 永远不 resolve
      if (window.AMap) {
        resolve(window.AMap as any);
        return;
      }
      existing.addEventListener('load', () => {
        if (window.AMap) resolve(window.AMap);
        else reject(new Error('AMap 尚未初始化完成'));
      });
      existing.addEventListener('error', () => reject(new Error('加载高德地图 JS 失败')));
      return;
    }

    const script = document.createElement('script');
    script.dataset.hisAmapLoader = '1';
    script.async = true;
    script.defer = true;
    // 预加载 AutoComplete（高德插件名：AutoComplete）
    script.src = `https://webapi.amap.com/maps?v=2.0&key=${encodeURIComponent(amapKey)}&plugin=AMap.AutoComplete`;
    script.onload = () => {
      if (window.AMap) resolve(window.AMap);
      else reject(new Error('AMap 尚未初始化完成'));
    };
    script.onerror = () => reject(new Error('加载高德地图 JS 失败'));
    document.head.appendChild(script);
  });

  return amapScriptPromise;
}

type AmapSuggestionItem = {
  id: string;
  name: string;
  address: string;
  location: [number, number] | null;
};

export interface LocationPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (payload: Record<string, any> | null, text: string | null) => void;
  disabled?: boolean;
  initialAddress?: string | null;
  initialPayload?: Record<string, any> | null;
  /** 对话框标题，默认「选择活动地点」 */
  title?: string;
  /** 对话框说明，默认描述搜索与地图选点 */
  description?: string;
}

export function LocationPickerDialog({
  open,
  onOpenChange,
  onPick,
  disabled,
  initialAddress,
  initialPayload,
  title = '选择活动地点',
  description = '输入关键字进行搜索；也可点击地图直接选点。',
}: LocationPickerDialogProps) {
  const runtimeEnv = window.__APP_ENV__;
  const amapKey =
    (import.meta as any).env?.VITE_AMAP_KEY ||
    runtimeEnv?.VITE_AMAP_KEY ||
    undefined;
  const securityJsCode =
    ((import.meta as any).env?.VITE_AMAP_SECURITY_JSCODE as string | undefined) ||
    runtimeEnv?.VITE_AMAP_SECURITY_JSCODE ||
    ((import.meta as any).env?.VITE_AMAP_SECURITY_JS_CODE as string | undefined) ||
    runtimeEnv?.VITE_AMAP_SECURITY_JS_CODE ||
    undefined;

  const onOpenChangeRef = React.useRef(onOpenChange);
  const onPickRef = React.useRef(onPick);
  onOpenChangeRef.current = onOpenChange;
  onPickRef.current = onPick;

  const handleModalOpenChange = React.useCallback((next: boolean) => {
    onOpenChangeRef.current(next);
  }, []);

  const [loading, setLoading] = React.useState(false);
  const [pickedAddress, setPickedAddress] = React.useState<string | null>(initialAddress ?? null);
  const [pickedPayload, setPickedPayload] = React.useState<Record<string, any> | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [suggestions, setSuggestions] = React.useState<AmapSuggestionItem[]>([]);

  const instanceId = React.useMemo(() => Math.random().toString(36).slice(2), []);
  const mapContainerId = React.useMemo(() => `his-amap-map-${instanceId}`, [instanceId]);
  const mapRef = React.useRef<any>(null);
  const markerRef = React.useRef<any>(null);
  const geocoderRef = React.useRef<any>(null);
  const autoCompleteRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (!open) return;
    setPickedAddress(initialAddress ?? null);
    setPickedPayload(initialPayload && typeof initialPayload === 'object' ? initialPayload : null);
    setSearchKeyword(initialAddress ?? '');
    setSuggestions([]);
    setError(null);
  }, [open, initialAddress, initialPayload]);

  React.useEffect(() => {
    if (!open) return;
    if (!amapKey) {
      setError('缺少高德地图 Key：请在环境变量中配置 VITE_AMAP_KEY');
      return;
    }

    let cancelled = false;
    setLoading(true);

    let map: any = null;
    let marker: any = null;

    (async () => {
      try {
        const AMap = await loadAmapJs(amapKey, securityJsCode);
        if (cancelled) return;
        // 地图初始化：即使 Autocomplete 失败，也要保证“地图可见 + 可点击选点”
        const center: [number, number] = [116.397428, 39.90923];
        map = new AMap.Map(mapContainerId, {
          zoom: 12,
          center,
          resizeEnable: true,
        });

        marker = new AMap.Marker({ position: center, draggable: false });
        map.add(marker);
        mapRef.current = map;
        markerRef.current = marker;

        // 显式加载 Geocoder，避免点击地图后只能回填经纬度
        if (typeof AMap?.plugin === 'function') {
          try {
            await new Promise<void>((resolve) => {
              AMap.plugin(['AMap.Geocoder'], () => resolve());
            });
          } catch {
            // ignore
          }
        }
        const canGeocode = typeof AMap.Geocoder === 'function';
        const geocoder = canGeocode ? new AMap.Geocoder({}) : null;
        geocoderRef.current = geocoder;

        const reverse = async (lng: number, lat: number) => {
          if (!geocoder) return `${lng},${lat}`;
          return new Promise<string | null>((resolve) => {
            geocoder.getAddress([lng, lat], (status: string, result: any) => {
              if (status !== 'complete') return resolve(`${lng},${lat}`);
              const formatted = result?.regeocode?.formattedAddress as string | undefined;
              resolve(formatted || `${lng},${lat}`);
            });
          });
        };

        const parseLngLat = (loc: any): [number, number] | null => {
          if (!loc) return null;
          if (typeof loc === 'string') {
            const [lng, lat] = loc.split(',').map((s: string) => Number(s));
            if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat];
            return null;
          }
          if (Array.isArray(loc) && loc.length >= 2) {
            const lng = Number(loc[0]);
            const lat = Number(loc[1]);
            if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat];
            return null;
          }
          if (typeof loc.getLng === 'function' && typeof loc.getLat === 'function') {
            const lng = Number(loc.getLng());
            const lat = Number(loc.getLat());
            if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat];
            return null;
          }
          if (typeof loc.lng !== 'undefined' && typeof loc.lat !== 'undefined') {
            const lng = Number(loc.lng);
            const lat = Number(loc.lat);
            if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat];
            return null;
          }
          return null;
        };

        const geocodeAddress = async (addressText: string): Promise<[number, number] | null> => {
          if (!geocoder) return null;
          return new Promise((resolve) => {
            geocoder.getLocation(addressText, (status: string, result: any) => {
              if (status !== 'complete') return resolve(null);
              const loc = result?.geocodes?.[0]?.location;
              resolve(parseLngLat(loc));
            });
          });
        };

        const locateCurrentPosition = async (): Promise<[number, number] | null> => {
          if (!navigator?.geolocation) return null;
          return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => resolve([pos.coords.longitude, pos.coords.latitude]),
              () => resolve(null),
              { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
            );
          });
        };

        const locateByAmapPlugin = async (): Promise<[number, number] | null> => {
          if (!AMap?.plugin || typeof AMap.plugin !== 'function') return null;
          return new Promise((resolve) => {
            AMap.plugin('AMap.Geolocation', () => {
              const GeolocationCtor = (AMap as any).Geolocation;
              if (typeof GeolocationCtor !== 'function') return resolve(null);
              const geolocation = new GeolocationCtor({
                enableHighAccuracy: true,
                timeout: 10000,
              });
              geolocation.getCurrentPosition((status: string, result: any) => {
                if (status !== 'complete') return resolve(null);
                const lng = Number(result?.position?.lng ?? result?.position?.getLng?.());
                const lat = Number(result?.position?.lat ?? result?.position?.getLat?.());
                if (Number.isFinite(lng) && Number.isFinite(lat)) {
                  resolve([lng, lat]);
                  return;
                }
                resolve(null);
              });
            });
          });
        };

        const initialLocationFromPayload = parseLngLat(initialPayload?.location);
        let initialLngLat: [number, number] | null = initialLocationFromPayload;
        if (!initialLngLat && initialAddress && initialAddress.trim()) {
          initialLngLat = await geocodeAddress(initialAddress.trim());
        }
        if (!initialLngLat) {
          initialLngLat = await locateCurrentPosition();
        }
        if (!initialLngLat) {
          initialLngLat = await locateByAmapPlugin();
        }
        if (initialLngLat) {
          const [lng, lat] = initialLngLat;
          marker.setPosition(initialLngLat);
          map.setCenter(initialLngLat);
          if (!initialPayload) {
            const addr = await reverse(lng, lat);
            setPickedAddress(addr);
            setPickedPayload({
              source: 'gaode-map',
              provider: 'browser.geolocation',
              name: '',
              address: addr || `${lng},${lat}`,
              location: {
                lat,
                lng,
                coordSystem: 'GCJ-02',
              },
            });
          }
        } else if (!initialPayload && !initialAddress) {
          const isSecure = window.isSecureContext;
          setError(
            isSecure
              ? '未能获取当前位置，请检查浏览器定位权限后重试。'
              : '未能获取当前位置：当前站点非 HTTPS/localhost，浏览器可能会阻止定位授权。'
          );
        }

        map.on('click', async (e: any) => {
          const lng = e?.lnglat?.getLng ? e.lnglat.getLng() : e?.lnglat?.lng;
          const lat = e?.lnglat?.getLat ? e.lnglat.getLat() : e?.lnglat?.lat;
          if (typeof lng !== 'number' || typeof lat !== 'number') return;

          const lnglat: [number, number] = [lng, lat];
          marker.setPosition(lnglat);
          map.setCenter(lnglat);

          const addr = await reverse(lng, lat);
          const payload = {
            source: 'gaode-map',
            provider: 'AMap.Map.click',
            name: '',
            address: addr || `${lng},${lat}`,
            location: {
              lat,
              lng,
              coordSystem: 'GCJ-02',
            },
          };
          setPickedAddress(addr);
          setPickedPayload(payload);
        });

        // 使用 JSAPI AutoComplete 作为联想数据源（与地图同一鉴权体系）
        let autoCompleteReady = typeof AMap.AutoComplete === 'function';
        try {
          if (!autoCompleteReady && AMap?.plugin && typeof AMap.plugin === 'function') {
            await new Promise<void>((resolve) => {
              AMap.plugin(['AMap.AutoComplete'], () => resolve());
            });
          }
        } catch {
          // ignore
        }
        for (let i = 0; i < 20 && !autoCompleteReady; i++) {
          if (typeof AMap.AutoComplete === 'function') autoCompleteReady = true;
          if (!autoCompleteReady) await sleep(100);
        }
        if (autoCompleteReady) {
          autoCompleteRef.current = new AMap.AutoComplete({ city: '' });
        } else {
          setError('地址联想初始化失败：请检查高德 Key 权限或安全密钥配置。');
        }
      } catch (e) {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : '初始化失败';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      try {
        // 避免异常：destroy 在某些版本可能不存在
        map?.destroy?.();
      } catch {
        // ignore
      }
      mapRef.current = null;
      markerRef.current = null;
      geocoderRef.current = null;
      autoCompleteRef.current = null;
      marker = null;
      map = null;
    };
  }, [open, amapKey, securityJsCode, mapContainerId, initialAddress, initialPayload]);

  React.useEffect(() => {
    if (!open) return;
    const keyword = searchKeyword.trim();
    if (!keyword) {
      setSuggestions([]);
      return;
    }
    if (!autoCompleteRef.current) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const parseLngLat = (loc: any): [number, number] | null => {
          if (!loc) return null;
          if (typeof loc === 'string') {
            const [lng, lat] = loc.split(',').map((s: string) => Number(s));
            return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null;
          }
          if (Array.isArray(loc) && loc.length >= 2) {
            const lng = Number(loc[0]);
            const lat = Number(loc[1]);
            return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null;
          }
          if (typeof loc.getLng === 'function' && typeof loc.getLat === 'function') {
            const lng = Number(loc.getLng());
            const lat = Number(loc.getLat());
            return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null;
          }
          if (typeof loc.lng !== 'undefined' && typeof loc.lat !== 'undefined') {
            const lng = Number(loc.lng);
            const lat = Number(loc.lat);
            return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null;
          }
          return null;
        };

        autoCompleteRef.current.search(keyword, (_status: string, result: any) => {
          if (cancelled) return;
          const tips = Array.isArray(result?.tips) ? result.tips : [];
          const next = tips
            .map((tip: any, idx: number) => {
              const name = String(tip?.name || '').trim();
              const address = String(tip?.address || '').trim();
              const location = parseLngLat(tip?.location);
              return {
                id: `${name || 'tip'}-${idx}`,
                name,
                address,
                location,
              };
            })
            .filter((it: any) => it.name || it.address);
          setSuggestions(next);
        });
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, searchKeyword, loading]);

  const handleSuggestionPick = React.useCallback(
    async (id: string) => {
      const picked = suggestions.find((s) => s.id === id);
      if (!picked) return;
      const display = [picked.name, picked.address].filter(Boolean).join('');
      setSearchKeyword(display);
      setPickedAddress(display || null);

      if (picked.location && markerRef.current && mapRef.current) {
        markerRef.current.setPosition(picked.location);
        mapRef.current.setCenter(picked.location);
      }

      setPickedPayload({
        source: 'gaode-map',
        provider: 'amap.inputtips',
        name: picked.name,
        address: picked.address || display,
        location: picked.location
          ? { lat: picked.location[1], lng: picked.location[0], coordSystem: 'GCJ-02' }
          : null,
      });
    },
    [suggestions]
  );

  const handleAutocompleteSelection = React.useCallback(
    (key: Key | null) => {
      if (key != null) void handleSuggestionPick(String(key));
    },
    [handleSuggestionPick]
  );

  const handleConfirmPick = React.useCallback(() => {
    onPickRef.current(pickedPayload, pickedAddress);
    onOpenChangeRef.current(false);
  }, [pickedPayload, pickedAddress]);

  const errorIsLocationWarning = Boolean(error?.includes('未能获取当前位置'));
  const alertStatus = errorIsLocationWarning ? 'warning' : 'danger';

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog className="flex max-h-[90vh] max-w-full flex-col sm:max-w-[900px]">
            <Modal.Header>
              <Modal.Heading className="text-xl font-medium">{title}</Modal.Heading>
              <Description className="text-muted text-sm">{description}</Description>
            </Modal.Header>

            <Modal.Body className="relative flex min-h-0 flex-1 flex-col gap-0 overflow-hidden p-1 gap-3">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <ComboBox
                    allowsCustomValue
                    allowsEmptyCollection
                    aria-label="搜索地点"
                    defaultFilter={() => true}
                    fullWidth
                    inputValue={searchKeyword}
                    isDisabled={disabled || loading}
                    items={suggestions}
                    menuTrigger="input"
                    variant="secondary"
                    onChange={handleAutocompleteSelection}
                    onInputChange={setSearchKeyword}
                  >
                    <Label className="sr-only">搜索地点</Label>
                    <ComboBox.InputGroup className="flex min-h-10 w-full items-stretch gap-1">
                      <Input
                        className="min-w-0 flex-1"
                        placeholder="搜索地点/输入地址"
                      />
                      <ComboBox.Trigger aria-label="展开或折叠地点建议" className="shrink-0" />
                    </ComboBox.InputGroup>
                    <ComboBox.Popover placement="bottom start">
                      <ListBox aria-label="地点建议列表" className="max-h-60">
                        {(item: AmapSuggestionItem) => (
                          <ListBox.Item id={item.id} textValue={`${item.name} ${item.address}`.trim()}>
                            <Label className="text-sm">{item.name || item.address}</Label>
                            {item.address ? (
                              <Description className="text-muted text-xs">{item.address}</Description>
                            ) : null}
                          </ListBox.Item>
                        )}
                      </ListBox>
                      {searchKeyword.trim().length > 0 && suggestions.length === 0 ? (
                        <EmptyState className="border-t border-[var(--border)] px-3 py-4 text-center text-muted text-sm">
                          无匹配地点
                        </EmptyState>
                      ) : null}
                    </ComboBox.Popover>
                  </ComboBox>
                </div>
              </div>

              {error ? (
                <div>
                  <Alert status={alertStatus}>
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Description>{error}</Alert.Description>
                    </Alert.Content>
                  </Alert>
                </div>
              ) : null}

              <div
                id={mapContainerId}
                role="region"
                aria-label="地图，点击选择地点"
                className="min-h-[360px] w-full flex-1"
                style={{ background: 'var(--surface-secondary)' }}
              />

              {loading ? (
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center bg-black/5"
                  role="status"
                  aria-live="polite"
                  aria-busy="true"
                >
                  <Spinner aria-label="地图加载中" />
                </div>
              ) : null}
            </Modal.Body>

            <Modal.Footer className="flex-shrink-0">
              <Button type="button" variant="ghost" isDisabled={disabled} onPress={() => onOpenChangeRef.current(false)}>
                取消
              </Button>
              <Button
                type="button"
                variant="primary"
                isDisabled={disabled || loading || !pickedPayload}
                onPress={handleConfirmPick}
              >
                使用该地址
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
